import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment-jalaali';
import EventIcon from '@mui/icons-material/Event';

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

export const toPersianDigits = (s: string): string =>
  s.replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)]);

export const toLatinDigits = (s: string): string =>
  s
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

interface JalaliDatePickerProps {
  value: string; // ISO date or "YYYY-MM-DD"
  onChange: (iso: string) => void;
  placeholder?: string;
  height?: number;
  /** If true, output is "YYYY-MM-DD" (gregorian). Default: full ISO string. */
  outputDateOnly?: boolean;
}

const JalaliDatePicker: React.FC<JalaliDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'انتخاب تاریخ',
  height = 38,
  outputDateOnly = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const jm = value ? moment(value) : moment();
  const jy = jm.jYear();
  const jmon = jm.jMonth();
  const jd = jm.jDate();
  const displayStr = value ? toPersianDigits(jm.format('jYYYY/jMM/jDD')) : placeholder;

  const [viewYear, setViewYear] = useState(jy);
  const [viewMonth, setViewMonth] = useState(jmon);

  useEffect(() => {
    if (open) {
      setViewYear(jy);
      setViewMonth(jmon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const monthNames = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
  ];
  const weekdays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

  const daysInMonth =
    viewMonth < 6 ? 31 : viewMonth < 11 ? 30 : moment.jIsLeapYear(viewYear) ? 30 : 29;
  const firstDay = moment(`${viewYear}/${viewMonth + 1}/1`, 'jYYYY/jM/jD');
  const firstWeekday = (firstDay.day() + 1) % 7;

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectDay = (d: number) => {
    const m = moment(`${viewYear}/${viewMonth + 1}/${d}`, 'jYYYY/jM/jD');
    onChange(outputDateOnly ? m.format('YYYY-MM-DD') : m.toISOString());
    setOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
  };

  const today = moment();

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          padding: '0 12px',
          height,
          background: 'var(--color-bg-paper)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 13,
          fontFamily: 'inherit',
          color: value ? 'var(--color-text-primary)' : 'var(--color-text-disabled)',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
        }}
      >
        <span>{displayStr}</span>
        <EventIcon sx={{ fontSize: 16, color: 'var(--color-text-secondary)' }} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            insetInlineEnd: 0,
            zIndex: 1300,
            background: 'var(--color-bg-paper)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: 10,
            width: 260,
            direction: 'rtl',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <button
              onClick={prevMonth}
              type="button"
              style={{
                width: 28, height: 28, border: 'none', background: 'transparent',
                cursor: 'pointer', borderRadius: 6, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: 'var(--color-text-secondary)', fontSize: 16,
              }}
            >
              ›
            </button>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {monthNames[viewMonth]} {toPersianDigits(String(viewYear))}
            </div>
            <button
              onClick={nextMonth}
              type="button"
              style={{
                width: 28, height: 28, border: 'none', background: 'transparent',
                cursor: 'pointer', borderRadius: 6, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: 'var(--color-text-secondary)', fontSize: 16,
              }}
            >
              ‹
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 2,
              marginBottom: 4,
            }}
          >
            {weekdays.map((w) => (
              <div
                key={w}
                style={{
                  fontSize: 10,
                  textAlign: 'center',
                  padding: 4,
                  color: 'var(--color-text-secondary)',
                  fontWeight: 600,
                }}
              >
                {w}
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const isSelected =
                value && viewYear === jy && viewMonth === jmon && d === jd;
              const isToday =
                viewYear === today.jYear() &&
                viewMonth === today.jMonth() &&
                d === today.jDate();
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => selectDay(d)}
                  style={{
                    height: 30,
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 12,
                    background: isSelected ? '#B25373' : isToday ? '#FCE8EE' : 'transparent',
                    color: isSelected ? '#fff' : isToday ? '#B25373' : 'var(--color-text-primary)',
                    fontWeight: isSelected || isToday ? 700 : 400,
                    fontFamily: 'inherit',
                  }}
                >
                  {toPersianDigits(String(d))}
                </button>
              );
            })}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 8,
              paddingTop: 8,
              borderTop: '1px solid var(--color-divider)',
            }}
          >
            <button
              type="button"
              onClick={() => {
                const m = moment();
                onChange(outputDateOnly ? m.format('YYYY-MM-DD') : m.toISOString());
                setOpen(false);
              }}
              style={{
                padding: '4px 10px', border: 'none', borderRadius: 6,
                cursor: 'pointer', fontSize: 11, fontWeight: 500,
                background: 'var(--color-bg-default)',
                color: 'var(--color-text-primary)', fontFamily: 'inherit',
              }}
            >
              امروز
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                padding: '4px 10px', border: 'none', borderRadius: 6,
                cursor: 'pointer', fontSize: 11, fontWeight: 500,
                background: 'transparent',
                color: 'var(--color-text-secondary)', fontFamily: 'inherit',
              }}
            >
              بستن
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JalaliDatePicker;
